import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, X } from "lucide-react";
import {
  acceptAll,
  rejectAll,
  saveCustom,
  hasStoredConsent,
  readConsent,
} from "../utils/consent";

export default function CookieBanner({ forceOpen, onClose }) {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang === "en" ? "en" : "it";
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setPrefs({
        functional: stored.functionality_storage === "granted",
        analytics: stored.analytics_storage === "granted",
        marketing: stored.ad_storage === "granted",
      });
    }
    if (!hasStoredConsent()) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (forceOpen) {
      setVisible(true);
      setShowPrefs(true);
    }
  }, [forceOpen]);

  const closeAll = () => {
    setVisible(false);
    setShowPrefs(false);
    if (onClose) onClose();
  };

  const onAccept = () => {
    acceptAll();
    closeAll();
  };
  const onReject = () => {
    rejectAll();
    closeAll();
  };
  const onSave = () => {
    saveCustom(prefs);
    closeAll();
  };

  if (!visible) return null;

  return (
    <div className="ec-cookie-root" role="dialog" aria-label="cookies">
      <div className="ec-cookie-card">
        <div className="ec-cookie-head">
          <div className="ec-cookie-title">
            <Cookie size={20} />
            <h3>{t("cookieBanner.title")}</h3>
          </div>
          {forceOpen > 0 ? (
            <button className="ec-icon-btn" aria-label="close" onClick={closeAll}>
              <X size={18} />
            </button>
          ) : null}
        </div>

        {!showPrefs && (
          <>
            <p className="ec-cookie-body">
              {t("cookieBanner.body")} {" "}
              <Link to={`/${currentLang}/cookies`} className="ec-cookie-link">
                {t("nav.cookies")}
              </Link>
              .
            </p>
            <div className="ec-cookie-actions">
              <button className="ec-btn ec-btn--ghost" onClick={() => setShowPrefs(true)}>
                {t("cookieBanner.manage")}
              </button>
              <button className="ec-btn ec-btn--secondary" onClick={onReject}>
                {t("cookieBanner.reject")}
              </button>
              <button className="ec-btn ec-btn--primary" onClick={onAccept}>
                {t("cookieBanner.accept")}
              </button>
            </div>
          </>
        )}

        {showPrefs && (
          <div className="ec-cookie-prefs">
            <h4>{t("cookieBanner.prefs_title")}</h4>
            <p className="ec-cookie-body">{t("cookieBanner.prefs_intro")}</p>

            <div className="ec-cat">
              <div className="ec-cat-head">
                <strong>{t("cookieBanner.cats.necessary.title")}</strong>
                <span className="ec-badge">✓</span>
              </div>
              <p>{t("cookieBanner.cats.necessary.body")}</p>
            </div>

            <div className="ec-cat">
              <div className="ec-cat-head">
                <strong>{t("cookieBanner.cats.functional.title")}</strong>
                <label className="ec-switch">
                  <input
                    type="checkbox"
                    checked={prefs.functional}
                    onChange={(e) => setPrefs({ ...prefs, functional: e.target.checked })}
                  />
                  <span />
                </label>
              </div>
              <p>{t("cookieBanner.cats.functional.body")}</p>
            </div>

            <div className="ec-cat">
              <div className="ec-cat-head">
                <strong>{t("cookieBanner.cats.analytics.title")}</strong>
                <label className="ec-switch">
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                  />
                  <span />
                </label>
              </div>
              <p>{t("cookieBanner.cats.analytics.body")}</p>
            </div>

            <div className="ec-cat">
              <div className="ec-cat-head">
                <strong>{t("cookieBanner.cats.marketing.title")}</strong>
                <label className="ec-switch">
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                  />
                  <span />
                </label>
              </div>
              <p>{t("cookieBanner.cats.marketing.body")}</p>
            </div>

            <div className="ec-cookie-actions">
              <button className="ec-btn ec-btn--ghost" onClick={() => setShowPrefs(false)}>
                {t("cookieBanner.back")}
              </button>
              <button className="ec-btn ec-btn--primary" onClick={onSave}>
                {t("cookieBanner.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
